import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { checkPostcode, METRO_COUNCILS, mockSubmitForm } from '../data/mock';
import { MapPin, Check, AlertCircle, ArrowRight, Loader2 } from 'lucide-react';

const ServiceAreaPage = () => {
  const [postcode, setPostcode] = useState('');
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [interestSubmitted, setInterestSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheck = (e) => {
    e.preventDefault();
    if (postcode.length >= 4) {
      const checkResult = checkPostcode(postcode);
      setResult(checkResult);
    }
  };

  const handleInterestSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    await mockSubmitForm('interest', { postcode, email });
    setIsSubmitting(false);
    setInterestSubmitted(true);
  };

  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#F8FAFC] to-white py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0D9488] text-white rounded-full text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Phase 1 Service Area
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#111111]">
              Metro Melbourne LGAs only
            </h1>
            <p className="mt-6 text-xl text-[#475569] leading-relaxed">
              We're focused on reliable metro service first. Check if your installation site 
              is within our current service area.
            </p>
          </div>
        </div>
      </section>

      {/* Postcode Checker */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl mx-auto">
            <Card className="border-[#E2E8F0] shadow-lg">
              <CardContent className="pt-8 pb-8">
                <h2 className="text-2xl font-bold text-[#111111] text-center mb-6">
                  Check your postcode
                </h2>
                <form onSubmit={handleCheck} className="flex flex-col sm:flex-row gap-4">
                  <Input
                    type="text"
                    placeholder="Enter postcode (e.g., 3000)"
                    value={postcode}
                    onChange={(e) => {
                      setPostcode(e.target.value.replace(/\D/g, '').slice(0, 4));
                      setResult(null);
                      setInterestSubmitted(false);
                    }}
                    className="flex-1 h-12 text-center text-lg font-medium border-[#E2E8F0] focus:border-[#0D9488]"
                  />
                  <Button 
                    type="submit" 
                    className="bg-[#0D9488] hover:bg-[#0F766E] text-white h-12 px-8"
                    disabled={postcode.length < 4}
                  >
                    Check
                  </Button>
                </form>

                {/* Result */}
                {result && (
                  <div className="mt-6 animate-fade-in">
                    {result.isServiced ? (
                      <div className="postcode-result-success rounded-xl p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Check className="w-6 h-6" />
                          <span className="font-bold text-lg">Great news!</span>
                        </div>
                        <p className="text-[#166534]">{result.message}</p>
                        <div className="mt-4 flex flex-col sm:flex-row gap-3">
                          <Link to="/book/waste" className="flex-1">
                            <Button className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white">
                              Book Collection
                            </Button>
                          </Link>
                          <Link to="/pricing" className="flex-1">
                            <Button variant="outline" className="w-full border-[#22C55E] text-[#166534] hover:bg-[#F0FDF4]">
                              Request Pricing
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="postcode-result-fail rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <AlertCircle className="w-6 h-6" />
                            <span className="font-bold">Not in service area yet</span>
                          </div>
                          <p>{result.message}</p>
                        </div>
                        
                        {!interestSubmitted ? (
                          <form onSubmit={handleInterestSubmit} className="bg-[#F8FAFC] rounded-xl p-6">
                            <p className="text-sm text-[#475569] mb-4">
                              Register your interest and we'll notify you when we expand to your area.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Input
                                type="email"
                                placeholder="Your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="flex-1 border-[#E2E8F0]"
                                required
                              />
                              <Button 
                                type="submit"
                                className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
                                disabled={isSubmitting}
                              >
                                {isSubmitting ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  'Register Interest'
                                )}
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <div className="bg-[#F0FDFA] rounded-xl p-6 text-center">
                            <Check className="w-8 h-8 text-[#0D9488] mx-auto mb-2" />
                            <p className="text-[#0F766E] font-medium">
                              Thanks! We'll notify you when we expand to your area.
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Metro Only */}
      <section className="section-padding bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-[#111111] mb-6">Why Metro Melbourne only?</h2>
            <p className="text-lg text-[#475569] leading-relaxed">
              We're committed to providing reliable, professional service. By focusing on Metropolitan 
              Melbourne Local Government Areas first, we can ensure consistent pickup schedules and 
              quality service before expanding further.
            </p>
          </div>
        </div>
      </section>

      {/* Metro Councils */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[#111111] mb-8">Example Metro Melbourne councils we service:</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {METRO_COUNCILS.slice(0, 20).map((council, index) => (
              <div 
                key={index} 
                className="flex items-center gap-2 text-sm text-[#475569] bg-white p-3 rounded-lg border border-[#E2E8F0]"
              >
                <Check className="w-4 h-4 text-[#0D9488] flex-shrink-0" />
                <span>{council}</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-sm text-[#64748B]">
            This is not an exhaustive list. Use the postcode checker above to confirm your specific location.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0D9488]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">In our service area?</h2>
          <p className="text-white/80 mb-8">Get started with Transa today</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/book/waste">
              <Button size="lg" className="bg-white text-[#0D9488] hover:bg-[#F0FDFA] px-8">
                Book Collection
              </Button>
            </Link>
            <Link to="/pricing">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8">
                Request Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServiceAreaPage;
